import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useDispatch } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { USER_DISPLAY_MSG } from '../../../Constants';
import runtimeConfigApi from '../../utils/runtimeConfigApi';
import { MEDIA_SERVER_LIVE_URL } from '../../utils/Constants';
import RuntimeConfigTabs from './RuntimeConfigTabs';
import RuntimeConfigStatusBar from './RuntimeConfigStatusBar';
import RuntimeConfigForm from './RuntimeConfigForm';
import RuntimeConfigBrowseModal from './RuntimeConfigBrowseModal';
import RuntimeConfigGrid from './RuntimeConfigGrid';
import ProxyEntryModal from './ProxyEntryModal';
import OverlapConfirmModal from './OverlapConfirmModal';
import PropagationBanner from './PropagationBanner';
import RemoveConfirmModal from './RemoveConfirmModal';
import {
  applyLocalDocumentChange,
  buildRemoveSuccessMessage,
  createEmptyDocument,
  getEntryCount,
  getEntryKey,
  getPropagationMessage,
  mergeMetadataFromResults,
  resolveAuthor,
} from './runtimeConfigUtils';
import {
  buildProxyEntryFromForm,
  getProxyExpectedVersion,
  getProxyEntryKey,
} from './proxyRouteUtils';
import './RuntimeConfigPanel.css';

const RuntimeConfigPanel = () => {
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const author = useMemo(() => resolveAuthor(accounts), [accounts]);
  const liveUrlBase = useMemo(() => MEDIA_SERVER_LIVE_URL, []);

  const [activeTab, setActiveTab] = useState('cors');
  const [metadata, setMetadata] = useState([]);
  const [documents, setDocuments] = useState({
    cors: null,
    frame: null,
    mime: null,
    proxy: null,
  });
  const [entryCounts, setEntryCounts] = useState({});
  const [notFoundTabs, setNotFoundTabs] = useState({});
  const [accessError, setAccessError] = useState('');
  const [loadingBrowse, setLoadingBrowse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [propagationMessage, setPropagationMessage] = useState('');
  const [prefill, setPrefill] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browsedTabs, setBrowsedTabs] = useState({});
  const [proxyEntryModal, setProxyEntryModal] = useState(null);
  const [overlapModal, setOverlapModal] = useState(null);

  const loadedTabsRef = useRef(new Set());
  const metadataRef = useRef([]);
  const documentsRef = useRef({});

  useEffect(() => {
    metadataRef.current = metadata;
  }, [metadata]);

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  const handleAccessDenied = useCallback(() => {
    setAccessError('Super Admin required');
  }, []);

  const loadMetadata = useCallback(async () => {
    try {
      const response = await runtimeConfigApi.fetchMetadata();
      if (response.status === 403) {
        handleAccessDenied();
        return;
      }
      if (response.status >= 400) {
        toastr.error('', response.data?.message || 'Failed to load runtime config metadata');
        return;
      }
      if (response.data?.configs) {
        setMetadata(response.data.configs);
      }
    } catch {
      toastr.error('', 'Failed to load runtime config metadata');
    }
  }, [handleAccessDenied]);

  const loadDocument = useCallback(async (configId, { force = false } = {}) => {
    if (!force && loadedTabsRef.current.has(configId)) {
      return documentsRef.current[configId] || true;
    }

    setLoadingBrowse(true);
    try {
      const response = await runtimeConfigApi.fetchDocument(configId);
      if (response.status === 403) {
        handleAccessDenied();
        return null;
      }
      if (response.status === 404) {
        setNotFoundTabs((prev) => ({ ...prev, [configId]: true }));
        const empty = createEmptyDocument(configId);
        setDocuments((prev) => ({ ...prev, [configId]: empty }));
        setEntryCounts((prev) => ({ ...prev, [configId]: 0 }));
        setBrowsedTabs((prev) => ({ ...prev, [configId]: true }));
        loadedTabsRef.current.add(configId);
        return empty;
      }
      if (response.status >= 400) {
        toastr.error('', response.data?.message || 'Failed to load config');
        return null;
      }
      if (response.data?.document) {
        const doc = response.data.document;
        setNotFoundTabs((prev) => ({ ...prev, [configId]: false }));
        setDocuments((prev) => ({ ...prev, [configId]: doc }));
        setEntryCounts((prev) => ({
          ...prev,
          [configId]: getEntryCount(configId, doc),
        }));
        setBrowsedTabs((prev) => ({ ...prev, [configId]: true }));
        loadedTabsRef.current.add(configId);
        return doc;
      }
    } catch {
      toastr.error('', 'Failed to load config');
    } finally {
      setLoadingBrowse(false);
    }
    return null;
  }, [handleAccessDenied]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  useEffect(() => {
    if (activeTab === 'proxy') {
      loadDocument('proxy');
    }
  }, [activeTab, loadDocument]);

  const resolveProxyExpectedVersion = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) {
      await loadMetadata();
      await loadDocument('proxy', { force: true });
    }
    const meta = metadataRef.current.find((item) => item.configId === 'proxy');
    const doc = documentsRef.current.proxy;
    let version = getProxyExpectedVersion(meta, doc);
    if (version == null) {
      await loadMetadata();
      const refreshedMeta = metadataRef.current.find((item) => item.configId === 'proxy');
      version = getProxyExpectedVersion(refreshedMeta, documentsRef.current.proxy);
    }
    if (version == null) {
      throw new Error('Proxy version unavailable. Refresh the page and try again.');
    }
    return version;
  }, [loadDocument, loadMetadata]);

  const dismissPropagationBanner = useCallback(() => {
    setPropagationMessage('');
  }, []);

  const applyProxyMutationSuccess = useCallback((
    response,
    {
      operation,
      entry,
      previousKey,
      suppressPropagationBanner = false,
      successAction = 'save',
    } = {}
  ) => {
    const message = getPropagationMessage(response.data?.propagation, { successAction });
    if (!suppressPropagationBanner) {
      setPropagationMessage(message);
    }
    if (response.data?.results?.length) {
      setMetadata((prev) => mergeMetadataFromResults(prev, response.data.results, author));
    }
    setDocuments((prev) => {
      const updated = applyLocalDocumentChange(
        'proxy',
        prev.proxy || createEmptyDocument('proxy'),
        operation,
        entry,
        previousKey
      );
      setEntryCounts((counts) => ({
        ...counts,
        proxy: getEntryCount('proxy', updated),
      }));
      return { ...prev, proxy: updated };
    });
    setBrowsedTabs((prev) => ({ ...prev, proxy: true }));
    return { success: true, message };
  }, [author]);

  const executeProxyMutation = useCallback(async ({
    operation,
    entry,
    previousKey = null,
    confirmOverlap = false,
    expectedVersion = null,
    retriedStale = false,
    suppressPropagationBanner = false,
    successAction = 'save',
  }) => {
    if (!author) {
      toastr.error('', 'Author could not be resolved from session');
      return { success: false };
    }

    setSubmitting(true);
    setAccessError('');

    try {
      let version = expectedVersion;
      let overlapConfirmed = confirmOverlap;
      let staleRetried = retriedStale;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (version == null) {
          version = await resolveProxyExpectedVersion(staleRetried);
        }

        const entryKey = getProxyEntryKey(entry);
        const keyChanged = Boolean(
          operation === 'merge' && previousKey && entryKey && previousKey !== entryKey
        );

        if (keyChanged) {
          const removeResponse = await runtimeConfigApi.applyChange({
            configId: 'proxy',
            operation: 'remove',
            author,
            entry: { prefix: previousKey },
            expectedVersion: version,
          });
          if (removeResponse.status === 403) {
            handleAccessDenied();
            return { success: false };
          }
          if (removeResponse.status === 409) {
            const errCode = removeResponse.data?.error;
            if (errCode === 'stale_version' && !staleRetried) {
              staleRetried = true;
              version = Number.isInteger(removeResponse.data.currentVersion)
                ? removeResponse.data.currentVersion
                : await resolveProxyExpectedVersion(true);
              continue;
            }
            if (errCode === 'overlap_confirmation_required' && !overlapConfirmed) {
              return {
                success: false,
                needsOverlapConfirm: true,
                overlaps: removeResponse.data.overlaps,
                pending: {
                  operation,
                  entry,
                  previousKey,
                  expectedVersion: removeResponse.data.currentVersion ?? version,
                  successAction,
                },
              };
            }
          }
          if (removeResponse.status >= 400) {
            toastr.error('', removeResponse.data?.message || 'Failed to remove previous entry');
            return { success: false };
          }
          if (removeResponse.data?.results?.length) {
            setMetadata((prev) => mergeMetadataFromResults(
              prev,
              removeResponse.data.results,
              author
            ));
            const proxyResult = removeResponse.data.results.find((item) => item.configId === 'proxy');
            if (proxyResult && Number.isInteger(proxyResult.version)) {
              version = proxyResult.version;
            }
          }
          setDocuments((prev) => {
            const updated = applyLocalDocumentChange(
              'proxy',
              prev.proxy || createEmptyDocument('proxy'),
              'remove',
              { prefix: previousKey }
            );
            setEntryCounts((counts) => ({
              ...counts,
              proxy: getEntryCount('proxy', updated),
            }));
            return { ...prev, proxy: updated };
          });
          previousKey = null;
        }

        const response = await runtimeConfigApi.applyChange({
          configId: 'proxy',
          operation,
          author,
          entry,
          expectedVersion: version,
          confirmOverlap: overlapConfirmed || undefined,
        });

        if (response.status === 403) {
          handleAccessDenied();
          return { success: false };
        }

        if (response.status === 409) {
          const errCode = response.data?.error;
          if (errCode === 'stale_version' && !staleRetried) {
            staleRetried = true;
            version = Number.isInteger(response.data.currentVersion)
              ? response.data.currentVersion
              : await resolveProxyExpectedVersion(true);
            continue;
          }
          if (errCode === 'overlap_confirmation_required' && !overlapConfirmed) {
            return {
              success: false,
              needsOverlapConfirm: true,
              overlaps: response.data.overlaps,
              pending: {
                operation,
                entry,
                previousKey,
                expectedVersion: response.data.currentVersion ?? version,
                successAction,
              },
            };
          }
          toastr.error('', response.data?.message || 'Failed to apply proxy change');
          return { success: false };
        }

        if (response.status >= 400) {
          const details = Array.isArray(response.data?.details)
            ? response.data.details.join('; ')
            : '';
          toastr.error('', details || response.data?.message || 'Failed to apply proxy change');
          return { success: false };
        }

        return applyProxyMutationSuccess(response, {
          operation,
          entry,
          previousKey,
          suppressPropagationBanner,
          successAction,
        });
      }

      toastr.error('', 'Another change was saved first. Refresh and try again.');
      return { success: false };
    } catch (error) {
      toastr.error('', error.message || 'Failed to apply proxy change');
      return { success: false };
    } finally {
      setSubmitting(false);
    }
  }, [
    applyProxyMutationSuccess,
    author,
    handleAccessDenied,
    resolveProxyExpectedVersion,
  ]);

  const handleBrowse = async () => {
    setBrowseOpen(true);
    await loadDocument(activeTab, { force: true });
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPrefill(null);
    setPropagationMessage('');
    setBrowseOpen(false);
    setProxyEntryModal(null);
    setOverlapModal(null);
  };

  const handleApply = async (
    { operation, entry, previousKey },
    { suppressPropagationBanner = false } = {}
  ) => {
    if (!author) {
      toastr.error('', 'Author could not be resolved from session');
      return { success: false };
    }
    setSubmitting(true);
    setAccessError('');
    try {
      const entryKey = getEntryKey(activeTab, entry);
      const keyChanged = Boolean(
        operation === 'merge' && previousKey && entryKey && previousKey !== entryKey
      );

      if (keyChanged) {
        const removeEntry = activeTab === 'mime'
          ? { extension: previousKey }
          : { pattern: previousKey };
        const removeResponse = await runtimeConfigApi.applyChange({
          configId: activeTab,
          operation: 'remove',
          author,
          entry: removeEntry,
        });
        if (removeResponse.status === 403) {
          handleAccessDenied();
          return { success: false };
        }
        if (removeResponse.status >= 400) {
          toastr.error('', removeResponse.data?.message || 'Failed to remove previous entry');
          return { success: false };
        }
      }

      const response = await runtimeConfigApi.applyChange({
        configId: activeTab,
        operation,
        author,
        entry,
      });
      if (response.status === 403) {
        handleAccessDenied();
        return { success: false };
      }
      if (response.status >= 400) {
        toastr.error('', response.data?.message || 'Failed to apply change');
        return { success: false };
      }

      const message = getPropagationMessage(response.data?.propagation);
      if (!suppressPropagationBanner) {
        setPropagationMessage(message);
      }
      if (response.data?.results?.length) {
        setMetadata((prev) => mergeMetadataFromResults(prev, response.data.results, author));
      }

      setDocuments((prev) => {
        const updated = applyLocalDocumentChange(
          activeTab,
          prev[activeTab] || createEmptyDocument(activeTab),
          operation,
          entry,
          previousKey
        );
        setEntryCounts((counts) => ({
          ...counts,
          [activeTab]: getEntryCount(activeTab, updated),
        }));
        return { ...prev, [activeTab]: updated };
      });
      if (browseOpen) {
        setBrowsedTabs((prev) => ({ ...prev, [activeTab]: true }));
      }
      return { success: true, message };
    } catch {
      toastr.error('', 'Failed to apply change');
      return { success: false };
    } finally {
      setSubmitting(false);
    }
  };

  const buildOverlapModalState = (result, removeContext = null) => ({
    overlaps: result.overlaps,
    pending: {
      ...result.pending,
      suppressPropagationBanner: removeContext
        ? true
        : result.pending?.suppressPropagationBanner,
      removeContext,
    },
  });

  const completeRemoveSuccess = useCallback(({
    target,
    fromForm,
    fromProxyEntryModal,
  }) => {
    const message = buildRemoveSuccessMessage(activeTab, target);
    dispatch({ type: USER_DISPLAY_MSG, response: { status_code: 200, message } });
    setRemoveModal(null);
    if (fromProxyEntryModal) {
      setProxyEntryModal(null);
    }
    if (fromForm) {
      setFormResetKey((key) => key + 1);
    }
  }, [activeTab, dispatch]);

  const handleRemoveRequest = (target, { fromForm = false } = {}) => {
    setRemoveModal({ target, fromForm });
  };

  const handleConfirmRemove = async () => {
    if (!removeModal?.target) {
      return;
    }
    const { target, fromForm, fromProxyEntryModal } = removeModal;
    const result = activeTab === 'proxy'
      ? await executeProxyMutation({
        operation: 'remove',
        entry: target,
        suppressPropagationBanner: true,
      })
      : await handleApply(
        { operation: 'remove', entry: target },
        { suppressPropagationBanner: true }
      );

    if (result.needsOverlapConfirm) {
      setRemoveModal(null);
      setOverlapModal(buildOverlapModalState(result, {
        target,
        fromForm,
        fromProxyEntryModal,
      }));
      return;
    }

    if (result.success) {
      completeRemoveSuccess({ target, fromForm, fromProxyEntryModal });
    }
  };

  const handleProxyEntryRemoveRequest = ({ prefix }) => {
    if (!prefix) {
      return;
    }
    setRemoveModal({
      target: { prefix },
      fromProxyEntryModal: true,
    });
  };

  const handleDismissRemoveModal = () => {
    setRemoveModal(null);
  };

  const clearPrefill = useCallback(() => setPrefill(null), []);

  const handleEdit = (prefillData) => {
    if (activeTab === 'proxy') {
      setProxyEntryModal({ mode: 'edit', prefill: prefillData });
      return;
    }
    setPrefill(prefillData);
    document.querySelector('.runtime-config-form-section')?.scrollIntoView?.({ behavior: 'smooth' });
  };

  const handleProxyEntryApply = async ({ formState, isEdit, editKey }) => {
    const entry = buildProxyEntryFromForm(formState);
    const result = await executeProxyMutation({
      operation: 'merge',
      entry,
      previousKey: isEdit ? editKey : null,
      successAction: isEdit ? 'update' : 'save',
    });

    if (result.needsOverlapConfirm) {
      setOverlapModal(buildOverlapModalState(result));
      return { success: false };
    }

    return result;
  };

  const handleOverlapConfirm = async () => {
    if (!overlapModal?.pending) {
      return;
    }
    const { pending } = overlapModal;
    const result = await executeProxyMutation({
      ...pending,
      confirmOverlap: true,
      expectedVersion: pending.expectedVersion,
    });
    if (result.success) {
      setOverlapModal(null);
      if (pending.removeContext) {
        completeRemoveSuccess(pending.removeContext);
      } else {
        setProxyEntryModal(null);
      }
    } else if (result.needsOverlapConfirm) {
      setOverlapModal(buildOverlapModalState(result, pending.removeContext || null));
    }
  };

  const handleDismissOverlapModal = () => {
    setOverlapModal(null);
  };

  const handleOpenProxyAddModal = () => {
    setProxyEntryModal({ mode: 'add' });
  };

  const handleCloseProxyEntryModal = () => {
    if (!submitting) {
      setProxyEntryModal(null);
    }
  };

  const activeMeta = metadata.find((item) => item.configId === activeTab);
  const isProxyTab = activeTab === 'proxy';

  if (accessError === 'Super Admin required') {
    return (
      <div className="runtime-config-panel" data-testid="runtime-config-panel">
        <div className="runtime-config-banner runtime-config-banner-error" role="alert">
          Super Admin required
        </div>
      </div>
    );
  }

  return (
    <div className="runtime-config-panel" data-testid="runtime-config-panel">
      <PropagationBanner message={propagationMessage} onDismiss={dismissPropagationBanner} />

      <RuntimeConfigTabs
        activeTab={activeTab}
        entryCounts={entryCounts}
        browsedTabs={browsedTabs}
        loadingBrowse={loadingBrowse}
        onTabChange={handleTabChange}
        onBrowse={handleBrowse}
        onAddEntry={handleOpenProxyAddModal}
      />

      <RuntimeConfigStatusBar
        configId={activeTab}
        metadataItem={activeMeta}
        document={documents[activeTab]}
        entryCount={entryCounts[activeTab]}
      />

      {isProxyTab ? (
        <div className="runtime-config-proxy-section" data-testid="runtime-config-proxy-section">
          <RuntimeConfigGrid
            configId="proxy"
            document={documents.proxy}
            loading={loadingBrowse && !documents.proxy}
            notFound={Boolean(notFoundTabs.proxy)}
            submitting={submitting}
            liveUrlBase={liveUrlBase}
            onEdit={handleEdit}
            onRemove={handleRemoveRequest}
            embedded
          />
        </div>
      ) : (
        <>
          <RuntimeConfigForm
            key={activeTab}
            configId={activeTab}
            prefill={prefill}
            submitting={submitting}
            formResetKey={formResetKey}
            onApply={handleApply}
            onRequestRemove={(entry) => handleRemoveRequest(entry, { fromForm: true })}
            onClear={() => setPrefill(null)}
            onClearPrefill={clearPrefill}
          />

          {browseOpen && (
            <RuntimeConfigBrowseModal
              configId={activeTab}
              document={documents[activeTab]}
              loading={loadingBrowse}
              notFound={Boolean(notFoundTabs[activeTab])}
              submitting={submitting}
              liveUrlBase={liveUrlBase}
              onEdit={handleEdit}
              onRemove={handleRemoveRequest}
              onClose={() => setBrowseOpen(false)}
            />
          )}
        </>
      )}

      <ProxyEntryModal
        open={Boolean(proxyEntryModal)}
        mode={proxyEntryModal?.mode || 'add'}
        initialPrefill={proxyEntryModal?.prefill || null}
        submitting={submitting}
        onApply={handleProxyEntryApply}
        onRemove={handleProxyEntryRemoveRequest}
        onClose={handleCloseProxyEntryModal}
      />

      <OverlapConfirmModal
        overlaps={overlapModal?.overlaps}
        operation={overlapModal?.pending?.operation}
        submitting={submitting && Boolean(overlapModal)}
        onConfirm={handleOverlapConfirm}
        onCancel={handleDismissOverlapModal}
      />

      <RemoveConfirmModal
        target={removeModal?.target}
        submitting={submitting && Boolean(removeModal?.target)}
        onConfirm={handleConfirmRemove}
        onCancel={handleDismissRemoveModal}
      />
    </div>
  );
};

export default RuntimeConfigPanel;
